/**
 * Every user-facing payments string of Wave 0 (manual-qa/payments-deep-dive-2026-10-18/dd2/FIX-PLAN.md,
 * "Messages" lines of W0-1/2/4/6/8/9/10/12), owned by ONE file so one lane owns the copy.
 *
 * §0 rule for every string: say (a) what happened to the money, (b) what the plan is now, (c) what to do
 * next. No elapsed-time suffix, no raw `Date.toString()`, never `undefined`. Every "Nothing was charged"
 * string ships under `payDecideBeforeCharge` / `checkoutStatusGate` only, where it is true by construction.
 * Callers pass already-formatted money ("$9.99") and dates ("Nov 24, 2026") — this file formats nothing.
 */
export const SUPPORT_EMAIL = 'support@xconvert.com';

// ---- W0-1 card checkout decides before it charges ----
/** 409 while another submit holds the checkout lease (CA-31). */
export const CHECKOUT_IN_PROGRESS = 'A payment is already in progress — wait a moment.';
export const cardDeclined = (stripeReason: string) =>
    `Your card was declined (${stripeReason}). No charge was made — try another card.`;
export const alreadyOnPlan = (plan: string, interval: string, provider: string) =>
    `You already have ${plan} (${interval}) via ${provider}. Nothing was charged.`;
export const useChangePlan = (currentPlan: string, requestedPlan: string) =>
    `You're on ${currentPlan}. To switch to ${requestedPlan} use Change plan — nothing was charged.`;
export const crossGateway = (provider: string) =>
    `You already have a ${provider} subscription. Cancel it from My Account first — nothing was charged.`;
export const THREE_DS_FAILED = "Card authentication didn't complete — no charge was made.";

// ---- W0-2 PayPal: no access before ACTIVATED ----
export const PAYPAL_PENDING =
    'PayPal approved your subscription and is activating it — your plan appears within a minute; nothing else to do. ' +
    `If it has not appeared in 10 minutes, do not buy again — contact ${SUPPORT_EMAIL}.`;
export const PAYPAL_POPUP_CLOSED = 'You closed PayPal before approving. Nothing was charged.';

// ---- W0-4 server-owned plan change ----
export const upgradePreview = (plan: string, chargedToday: string, nextAmount: string, nextDate: string) =>
    `Switch to ${plan}: ${chargedToday} charged today (prorated for the rest of your current period), then ${nextAmount} on ${nextDate}.`;
/** MON-13 (3): the credit settles that invoice first (`invoice.paid`, amount_paid 0) — "next charge $Y" would be false. */
export const downgradePreview = (plan: string, credit: string, nextAmount: string, nextDate: string) =>
    `Switch to ${plan}: a credit of ${credit} is applied to your next invoices (nothing is refunded to your card); next invoice ${nextAmount} on ${nextDate}, paid from that credit first.`;
export const PLAN_CHANGE_PAYPAL_UNAVAILABLE =
    `Plan changes for PayPal subscriptions are handled by support for now — email ${SUPPORT_EMAIL}. Nothing was charged.`;
export const PLAN_CHANGE_RESUMES = 'This also resumes your subscription (cancellation removed).';

// ---- W0-6 disputes ----
export const disputeLost = (amount: string, date: string) =>
    `Your bank reversed the payment of ${amount} on ${date}; the subscription has ended. Contact ${SUPPORT_EMAIL} if this is wrong.`;

// ---- W0-8 API billing gate ----
export const API_BILLING_UNAVAILABLE = "API billing isn't available yet";

// ---- W0-9 cancel tells the truth ----
export const cancelled = (plan: string, provider: string, accessUntil: string) =>
    `Cancelled ${plan} (${provider}). Access continues until ${accessUntil}; no further charges.`;
export const CANCELLED_PAST_DUE = 'Cancelled now; the unpaid invoice was voided — nothing more will be charged.';
/** MON-13 (1): provider-aware — a Stripe `incomplete` sub cancelled now was never completed at Stripe, not at PayPal. */
export const neverCompleted = (provider: string) =>
    `That subscription was never completed at ${provider}, so nothing was charged; your account is now Free.`;
/** MON-13 (1): `outcome: NOT_FOUND_AT_PROVIDER` — the provider has no such subscription any more (purged / deleted). */
export const notFoundAtProvider = (provider: string) =>
    `That subscription no longer exists at ${provider} — nothing more will be charged; your account is now Free.`;
/** @deprecated MON-13 (1): kept for older callers; use {@link neverCompleted}(provider). */
export const CANCELLED_NEVER_COMPLETED = neverCompleted('PayPal');
export const NOTHING_TO_CANCEL = 'You have no active subscription to cancel.';
/** TES-4: §0 (a) money + (c) next step, not just the date. */
export const alreadyScheduledToEnd = (date: string) => `Already scheduled to end on ${date}; no further charges. Nothing else to do.`;
/** CR-L4-1: `outcome: CANCELLED_NOW` (past_due cancelled now, no open invoice to void). */
export const CANCELLED_NOW = 'Cancelled now; nothing more will be charged.';
/** CR-L4-1: one sentence per `failed[]` item; the backend's cancel is idempotent, so retrying is safe. */
export const cancelFailed = (provider: string, id: string) => `We couldn't cancel ${provider} ${id}; retrying is safe.`;

// ---- W0-10 truth hotfix (the only 409 text that may ship before W0-1) ----
export const duplicateChargedAndCancelled = (amount: string, reference: string) =>
    `Your card was charged ${amount} and the duplicate subscription was cancelled. Contact ${SUPPORT_EMAIL} with reference ${reference}.`;

// ---- W0-12 status gate + server-side PayPal create ----
export const paypalRefunded = (amount: string, reason: string) =>
    `Your PayPal payment of ${amount} was refunded and the subscription cancelled because ${reason}.`;
export const PAYPAL_FINISHING_PREVIOUS = 'Finishing your previous PayPal approval…';
/** OD-14: the current plan's button is shown disabled with this label. */
export const CURRENT_PLAN = 'Current plan';
export const changeTo = (plan: string) => `Change to ${plan}`;
export const UPDATE_YOUR_CARD = 'Update your card';

// ---- L5-web additions (labels and the messages Wave 0 implies but does not spell out; LEDGER L5 rulings) ----
/** W0-1 step 3 `{pending}` for a card whose PaymentIntent is `processing` (no 3DS, not yet settled). */
export const CARD_PROCESSING =
    'Your card payment is processing — your plan appears within a minute; nothing else to do. ' +
    `If it has not appeared in 10 minutes, do not buy again — contact ${SUPPORT_EMAIL}.`;
/** W0-1 step 7: title of the decline dialog (body = `cardDeclined(reason)` or the server's text). */
export const CARD_DECLINED_TITLE = 'Card declined';
/** W0-12 (2) `PAYMENT_UPDATE_REQUIRED`: the status call carries no amount/date, so the message states only what is true. */
export const paymentUpdateRequired = (plan: string) =>
    `Your ${plan} payment failed; update your card — no new subscription is needed. Nothing was charged.`;
/** W0-4 applied (not preview) — same numbers as the preview, past tense. */
export const upgradeApplied = (plan: string, chargedToday: string, nextAmount: string, nextDate: string) =>
    `Switched to ${plan}: ${chargedToday} was charged today (prorated for the rest of your current period); next charge ${nextAmount} on ${nextDate}.`;
export const downgradeApplied = (plan: string, credit: string, nextAmount: string, nextDate: string) =>
    `Switched to ${plan}: a credit of ${credit} is applied to your next invoices (nothing is refunded to your card); next invoice ${nextAmount} on ${nextDate}, paid from that credit first.`;
export const PLAN_CHANGE_TITLE = 'Change plan';
export const PLAN_CHANGED_TITLE = 'Plan changed';
/** UT-05 / NF8: a PayPal subscriber's "Update payment" never reaches Stripe's portal — say where the card lives instead. */
export const PAYPAL_UPDATE_PAYMENT_AT_PAYPAL =
    'Your subscription is billed by PayPal — update your payment method in your PayPal account ' +
    '(paypal.com → Settings → Payments → Automatic payments). Nothing was charged.';

// ---- W0-10 state labels (current-plan-summary + /portal); callers pass formatted dates ----
export const renewsOn = (date: string) => `Renews ${date}`;
export const endsOn = (date: string) => `Ends ${date}`;
export const PAYMENT_FAILED_LABEL = 'Payment failed';
export const SUSPENDED_LABEL = 'Suspended';
export const cancelledActiveUntil = (date: string) => `Cancelled (active until ${date})`;
/** Today's cancel toast, kept verbatim for `cancelV2` off (and for a server that sends no `cancelled` list). */
export const CANCELLED_LEGACY_TOAST = 'Subscription cancelled successfully.';
